import { NgFor, NgIf } from '@angular/common'
import { Component, DestroyRef, EventEmitter, inject, Input, OnInit, Output } from '@angular/core'
import { takeUntilDestroyed } from '@angular/core/rxjs-interop'
import { Router } from '@angular/router'
import { ContactlessOptionsEntity, ContactlessOptionsPatchPayload, ContactlessOptionsService } from '@app/modules/admin/services/contactless-options.service'
import { AuthService } from '@app/modules/auth/services/auth.service'
import { ServerRepresentedEvent } from '@app/modules/events/models/event.model'
import { EventService } from '@app/modules/events/services/event.service'
import { PersonaTypes } from '@app/modules/personas/enums/persona-types.enum'
import { PersonaField } from '@app/modules/personas/interfaces/persona-field.interface'
import { Persona } from '@app/modules/personas/interfaces/persona.interface'
import { PersonaService } from '@app/modules/personas/services/persona.service'
import { ToastrService } from '@app/services/toastr.service'
import { forkJoin, Observable, of, Subject } from 'rxjs'
import { catchError, finalize, takeUntil } from 'rxjs/operators'
import { hiddenOptionKeysForEntity, optionsDefaultsForEntity, optionsGroupsForEntity } from './options-settings.groups'
import { OptionsTreeEditorComponent } from './options-tree-editor/options-tree-editor.component'

type OptionsStep = 'personas' | 'fields' | 'editor'

interface EventWithForms extends Partial<ServerRepresentedEvent> {
  detailedForms?: Persona[]
}

@Component({
  selector: 'ngx-admin-options',
  standalone: true,
  templateUrl: './options.component.html',
  host: { class: 'tw-block tw-h-full' },
  imports: [NgFor, NgIf, OptionsTreeEditorComponent],
  providers: [EventService, ContactlessOptionsService]
})
export class OptionsComponent implements OnInit {
  @Input() initialEvent: EventWithForms | null = null
  @Output() exit = new EventEmitter<void>()

  public entityType: ContactlessOptionsEntity = 'event'
  public step: OptionsStep = 'editor'

  public selectedEvent: EventWithForms | null = null
  public selectedForm: Persona | null = null
  public selectedField: PersonaField | null = null
  public personas: Persona[] = []
  public fields: PersonaField[] = []

  public isLoadingForms = false
  public isLoadingOptions = false
  public isSaving = false

  public editableOptions: Record<string, unknown> = {}
  public pendingUnset: string[] = []
  public loadedEntityLabel = ''
  public hasOptions = false

  private _originalOptions: Record<string, unknown> = {}
  private _originalEditLabel = ''
  private readonly _destroyRef = inject(DestroyRef)
  private readonly _destroy$ = new Subject<void>()

  constructor(
    private readonly _router: Router,
    private readonly _authService: AuthService,
    private readonly _eventService: EventService,
    private readonly _optionsService: ContactlessOptionsService,
    private readonly _personaService: PersonaService,
    private readonly _toastr: ToastrService
  ) {}

  ngOnInit(): void {
    if (!this._authService.user?.is_admin) {
      this._router.navigate(['/'])
      return
    }

    if (!this._authService.currentWorkspace) {
      this._router.navigate(['/workspaces'])
      return
    }

    this._destroyRef.onDestroy(() => {
      this._destroy$.next()
      this._destroy$.complete()
    })

    if (!this.initialEvent?.id) {
      this._toastr.error('This event has no ID', 'Error')
      this.exit.emit()
      return
    }

    this.selectedEvent = this.initialEvent
    this.personas = (this.initialEvent.detailedForms || this.initialEvent.forms || []) as Persona[]
    this.entityType = 'event'
    this._continueWithSelectedEvent()
  }

  public get headerSubtitle(): string {
    if (this.step === 'personas') {
      return 'Choose which persona (registration form) to update'
    }
    return `Editing settings for ${this.loadedEntityLabel || 'selection'}`
  }

  public get showPersonaEntry(): boolean {
    return this.step === 'editor' && this.entityType === 'event' && !!this.selectedEvent
  }

  public get settingsGroups() {
    return optionsGroupsForEntity(this.entityType)
  }

  /** Stable arrays so the editor input does not rebuild on every change detection. */
  private readonly _hideNotifyParent = ['notifyParent']
  private readonly _hideInvites = ['allowInvitation', 'allowImport']
  private readonly _hideNotifyParentAndInvites = ['notifyParent', 'allowInvitation', 'allowImport']
  private readonly _noHiddenSettingKeys: string[] = []

  /**
   * Notify parent is only on the group child. Invite and import are only on the group persona.
   */
  public get hiddenSettingKeys(): string[] {
    if (this.entityType !== 'form') {
      return this._noHiddenSettingKeys
    }
    const isGroup = this._isGroupForm(this.selectedForm)
    const isChild = this._isGroupFormChild(this.selectedForm)
    if (isGroup) return this._hideNotifyParent
    if (isChild) return this._hideInvites
    return this._hideNotifyParentAndInvites
  }

  public get settingsDefaults(): Record<string, unknown> | null {
    return optionsDefaultsForEntity(this.entityType)
  }

  public get canApply(): boolean {
    return this._resolvedEntityId() !== null && !this.isLoadingOptions && !this.isSaving
  }

  public get showSettingsEditor(): boolean {
    return this.hasOptions || !!this.settingsDefaults
  }

  public openPersonaSettings(): void {
    this.entityType = 'form'
    this.selectedForm = null
    this.selectedField = null
    this.fields = []
    this._clearEditorState(false)
    this._loadPersonasStep()
  }

  public backToEventSettings(): void {
    this.entityType = 'event'
    this.selectedForm = null
    this.selectedField = null
    this.fields = []
    this._clearEditorState(false)
    if (this.selectedEvent?.id) {
      this._openEditor('event', this.selectedEvent.id, this.selectedEvent.name || `Event #${this.selectedEvent.id}`)
    }
  }

  private _loadPersonasStep(): void {
    const event = this.selectedEvent
    if (!event?.id) {
      this._toastr.error('This event has no ID', 'Error')
      return
    }

    this.personas = (event.detailedForms || event.forms || []) as Persona[]
    this.step = 'personas'
    if (this.personas.length) {
      return
    }

    this.isLoadingForms = true
    this._eventService
      .getEventForms(event.id)
      .pipe(
        takeUntil(this._destroy$),
        catchError(() => {
          this._toastr.error('Failed to load personas for this event', 'Error')
          return of([] as Persona[])
        }),
        finalize(() => {
          this.isLoadingForms = false
        })
      )
      .subscribe((forms) => {
        this.personas = forms || []
      })
  }

  private _continueWithSelectedEvent(): void {
    const event = this.selectedEvent
    if (!event?.id) {
      this._toastr.error('This event has no ID', 'Error')
      return
    }

    if (this.entityType === 'event') {
      this._openEditor('event', event.id, event.name || `Event #${event.id}`)
      return
    }

    this._loadPersonasStep()
  }

  public selectPersona(persona: Persona): void {
    this.selectedForm = persona
    this.selectedField = null
    this.fields = []
    this._clearEditorState()
    this._openEditor('form', persona.id, this.getPersonaDisplayName(persona))
  }

  public backToEvents(): void {
    this.exit.emit()
  }

  public backToPersonas(): void {
    this.selectedForm = null
    this.selectedField = null
    this.fields = []
    this._clearEditorState()
    this.step = 'personas'
  }

  public onUnsetPath(path: string): void {
    if (!this._isValidPath(path)) {
      return
    }
    this._deleteAtPath(this.editableOptions, path)
    if (!this.pendingUnset.includes(path)) {
      this.pendingUnset = [...this.pendingUnset, path]
    }
    this.editableOptions = this._deepCloneObject(this.editableOptions)
    this.hasOptions = Object.keys(this.editableOptions).length > 0
  }

  public onRestorePath(path: string): void {
    if (!this._isValidPath(path)) {
      return
    }
    const defaults = this.settingsDefaults
    if (!defaults) {
      return
    }
    const value = this._getAtPath(defaults, path)
    if (value === undefined) {
      return
    }
    this._setAtPath(this.editableOptions, path, this._cloneValue(value))
    this.pendingUnset = this.pendingUnset.filter((item) => item !== path && !item.startsWith(`${path}.`))
    this.editableOptions = this._deepCloneObject(this.editableOptions)
    this.hasOptions = Object.keys(this.editableOptions).length > 0
  }

  public applyPatch(): void {
    const entityId = this._resolvedEntityId()
    if (entityId === null) {
      this._toastr.error('Select an event, persona, or field first', 'Validation Error')
      return
    }

    const invalidUrl = this._findInvalidUrl(this.editableOptions)
    if (invalidUrl) {
      this._toastr.error(`Invalid URL at ${invalidUrl}`, 'Validation Error')
      return
    }

    const labelChanged = this._editLabelChanged()
    const payload = this._buildPayload()
    if (!payload && !labelChanged) {
      this._toastr.error('No changes to save', 'Validation Error')
      return
    }

    const confirmationPayload = labelChanged ? this._confirmationLabelPayload() : null
    if (labelChanged && !confirmationPayload) {
      this._toastr.error('Select an event and persona before saving the edit button text', 'Validation Error')
      return
    }

    const savedLabel = this._currentEditLabel()
    const saves: Observable<unknown>[] = []
    let optionsIndex = -1
    if (payload) {
      optionsIndex = saves.length
      saves.push(this._optionsService.patchOptions(this.entityType, entityId, payload))
    }
    if (confirmationPayload) {
      saves.push(this._personaService.updateConfirmationSettings(confirmationPayload))
    }

    this.isSaving = true
    forkJoin(saves)
      .pipe(
        takeUntilDestroyed(this._destroyRef),
        finalize(() => {
          this.isSaving = false
        })
      )
      .subscribe({
        next: (responses) => {
          if (labelChanged && this.selectedForm) {
            this.selectedForm = { ...this.selectedForm, editLabel: savedLabel } as Persona
          }
          if (optionsIndex >= 0) {
            const response = responses[optionsIndex] as { data?: { options?: unknown } }
            this._hydrateOptions(response?.data?.options ?? {})
          } else if (labelChanged) {
            this._rememberEditLabel(savedLabel)
          }
          this._toastr.success('Settings saved', 'Success')
        },
        error: (err) => {
          this._toastr.error(this._errorMessage(err, 'Failed to save settings'), 'Error')
        }
      })
  }

  private _isGroupForm(persona: Persona | null): boolean {
    return Number(persona?.type) === PersonaTypes.GROUP
  }

  private _isGroupFormChild(persona: Persona | null): boolean {
    return persona?.parent != null
  }

  public getPersonaDisplayName(persona: Persona): string {
    return persona?.name || `Persona #${persona?.id}`
  }

  public getPersonaType(persona: Persona): string {
    switch (Number(persona?.type)) {
      case PersonaTypes.VISITOR:
        return 'Visitor'
      case PersonaTypes.VIP:
        return 'VIP'
      case PersonaTypes.GENERIC:
        return 'Generic'
      case PersonaTypes.GROUP:
        return 'Group'
      case PersonaTypes.MEMBER:
        return 'Member'
      default:
        return 'Registration'
    }
  }

  public getFieldCount(persona: Persona): number {
    return persona?.form_elements?.length || 0
  }

  private _openEditor(entity: ContactlessOptionsEntity, id: number, label: string): void {
    this.entityType = entity
    this.loadedEntityLabel = label
    this.step = 'editor'
    this.isLoadingOptions = true
    this._clearEditorState(false)

    this._optionsService
      .getOptions(entity, id)
      .pipe(
        takeUntilDestroyed(this._destroyRef),
        finalize(() => {
          this.isLoadingOptions = false
        })
      )
      .subscribe({
        next: (response) => {
          this._hydrateOptions(response?.data?.options ?? {}, response?.data?.editLabel)
        },
        error: (err) => {
          this._hydrateOptions({})
          this._toastr.error(this._errorMessage(err, 'Failed to load settings'), 'Error')
        }
      })
  }

  private _hydrateOptions(options: unknown, apiEditLabel?: unknown): void {
    // Only show what the API returned — catalog defaults are for Add chips, not pre-selected.
    // Drop keys owned by other screens so they stay out of Other and out of this PATCH.
    const loaded = this._deepCloneObject(options)
    for (const key of hiddenOptionKeysForEntity(this.entityType)) {
      delete loaded[key]
    }
    this._applyPersonaEditLabel(loaded, apiEditLabel)
    this._originalOptions = this._deepCloneObject(loaded)
    this.editableOptions = loaded
    this.pendingUnset = []
    this.hasOptions = Object.keys(this.editableOptions).length > 0
  }

  /**
   * Persona confirmation button text, shown in the Confirmation section.
   * The form options response is the source of truth (`data.editLabel`).
   */
  private _applyPersonaEditLabel(loaded: Record<string, unknown>, apiEditLabel?: unknown): void {
    if (this.entityType !== 'form') {
      this._originalEditLabel = ''
      return
    }
    const persona = (this.selectedForm || {}) as Record<string, unknown>
    const text = this._labelText(apiEditLabel) || this._labelText(loaded['editLabel']) || this._labelText(persona['editLabel'] ?? persona['edit_label']) || this._defaultEditLabel()
    loaded['editLabel'] = text
    this._originalEditLabel = text
  }

  private _labelText(value: unknown): string {
    return typeof value === 'string' ? value.trim() : ''
  }

  private _defaultEditLabel(): string {
    return Number(this.selectedForm?.type) === PersonaTypes.GROUP ? 'Access the portal' : 'Edit your info'
  }

  private _currentEditLabel(): string {
    const raw = this.editableOptions['editLabel']
    const text = typeof raw === 'string' ? raw.trim() : ''
    return text || this._defaultEditLabel()
  }

  private _editLabelChanged(): boolean {
    return this.entityType === 'form' && this._currentEditLabel() !== this._originalEditLabel
  }

  private _rememberEditLabel(label: string): void {
    this.editableOptions = { ...this.editableOptions, editLabel: label }
    this._originalOptions = { ...this._originalOptions, editLabel: label }
    this._originalEditLabel = label
    this.pendingUnset = this.pendingUnset.filter((path) => path !== 'editLabel' && !path.startsWith('editLabel.'))
  }

  private _confirmationLabelPayload(): {
    workspace_id: number
    event_id: number
    form_id: number
    editLabel: string
    options: Record<string, boolean>
  } | null {
    const workspaceId = this._authService.currentWorkspace?.id
    const eventId = this.selectedEvent?.id
    const formId = this.selectedForm?.id
    if (!workspaceId || !eventId || !formId) {
      return null
    }
    const flag = (key: string): boolean => {
      const value = this.editableOptions[key]
      if (value === true || value === 1) return true
      if (typeof value === 'string') {
        const normalized = value.trim().toLowerCase()
        return normalized === 'true' || normalized === '1'
      }
      return false
    }
    return {
      workspace_id: workspaceId,
      event_id: eventId,
      form_id: formId,
      editLabel: this._currentEditLabel(),
      options: {
        hideQrCode: flag('hideQrCode'),
        hideDateTime: flag('hideDateTime'),
        hideLocation: flag('hideLocation'),
        hideWalletButton: flag('hideWalletButton'),
        hideEditButton: flag('hideEditButton')
      }
    }
  }

  private _resolvedEntityId(): number | null {
    if (this.entityType === 'event') {
      return this.selectedEvent?.id ?? null
    }
    if (this.entityType === 'form') {
      return this.selectedForm?.id ?? null
    }
    return this.selectedField?.id ?? null
  }

  private _clearEditorState(clearLabel = true): void {
    this.editableOptions = {}
    this._originalOptions = {}
    this._originalEditLabel = ''
    this.pendingUnset = []
    this.hasOptions = false
    if (clearLabel) {
      this.loadedEntityLabel = ''
    }
  }

  private _buildPayload(): ContactlessOptionsPatchPayload | null {
    const changedSet = this._diffForSet(this._originalOptions, this.editableOptions)
    if (changedSet && 'editLabel' in changedSet) {
      delete changedSet.editLabel
    }
    const unset = this.pendingUnset.filter((path) => path !== 'editLabel' && !path.startsWith('editLabel.'))

    if ((!changedSet || Object.keys(changedSet).length === 0) && unset.length === 0) {
      return null
    }

    const payload: ContactlessOptionsPatchPayload = {}
    if (changedSet && Object.keys(changedSet).length > 0) {
      payload.set = changedSet
    }
    if (unset.length > 0) {
      payload.unset = unset
    }
    return payload
  }

  /** Build a nested `set` object. A changed object is sent whole — a partial theme replaces the stored theme and drops the other fields. */
  private _diffForSet(original: unknown, current: unknown): Record<string, unknown> | null {
    if (this._isPlainObject(current) && this._isPlainObject(original)) {
      const result: Record<string, unknown> = {}
      for (const key of Object.keys(current)) {
        if (!(key in original) || !this._deepEqual(original[key], current[key])) {
          result[key] = this._cloneValue(current[key])
        }
      }
      return result
    }

    if (!this._deepEqual(original, current) && this._isPlainObject(current)) {
      return current
    }
    return null
  }

  private _isValidPath(path: string): boolean {
    return !!path && !path.startsWith('.') && !path.endsWith('.') && !path.includes('..')
  }

  private _findInvalidUrl(value: unknown, path = ''): string | null {
    if (this._isPlainObject(value)) {
      for (const key of Object.keys(value)) {
        const childPath = path ? `${path}.${key}` : key
        const found = this._findInvalidUrl(value[key], childPath)
        if (found) {
          return found
        }
      }
      return null
    }
    if (typeof value !== 'string' || !value.trim()) {
      return null
    }
    const leaf = path.includes('.') ? path.slice(path.lastIndexOf('.') + 1) : path
    if (!/(link|url|logo|front_fixed_element)$/i.test(leaf)) {
      return null
    }
    try {
      const parsed = new URL(value.trim())
      if (parsed.protocol === 'http:' || parsed.protocol === 'https:') {
        return null
      }
    } catch {
      // fall through
    }
    return path
  }

  private _getAtPath(root: Record<string, unknown>, path: string): unknown {
    const parts = path.split('.').filter(Boolean)
    let cursor: unknown = root
    for (const part of parts) {
      if (!this._isPlainObject(cursor) || !(part in cursor)) {
        return undefined
      }
      cursor = cursor[part]
    }
    return cursor
  }

  private _setAtPath(root: Record<string, unknown>, path: string, value: unknown): void {
    const parts = path.split('.').filter(Boolean)
    if (!parts.length) {
      return
    }
    let cursor: Record<string, unknown> = root
    for (let i = 0; i < parts.length - 1; i++) {
      const part = parts[i]
      const next = cursor[part]
      if (!this._isPlainObject(next)) {
        cursor[part] = {}
      }
      cursor = cursor[part] as Record<string, unknown>
    }
    cursor[parts[parts.length - 1]] = value
  }

  private _deleteAtPath(root: Record<string, unknown>, path: string): void {
    const parts = path.split('.').filter(Boolean)
    if (!parts.length) {
      return
    }
    let cursor: Record<string, unknown> = root
    for (let i = 0; i < parts.length - 1; i++) {
      const part = parts[i]
      const next = cursor[part]
      if (!this._isPlainObject(next)) {
        return
      }
      cursor = next
    }
    delete cursor[parts[parts.length - 1]]
  }

  private _cloneValue(value: unknown): unknown {
    if (value === null || typeof value !== 'object') {
      return value
    }
    try {
      return JSON.parse(JSON.stringify(value))
    } catch {
      return value
    }
  }

  private _deepCloneObject(value: unknown): Record<string, unknown> {
    if (!this._isPlainObject(value)) {
      return {}
    }
    try {
      return JSON.parse(JSON.stringify(value)) as Record<string, unknown>
    } catch {
      return {}
    }
  }

  private _isPlainObject(value: unknown): value is Record<string, unknown> {
    return value !== null && typeof value === 'object' && !Array.isArray(value)
  }

  private _deepEqual(a: unknown, b: unknown): boolean {
    try {
      return JSON.stringify(a) === JSON.stringify(b)
    } catch {
      return a === b
    }
  }

  private _errorMessage(err: unknown, fallback: string): string {
    const message = (err as { error?: { message?: string } })?.error?.message
    return typeof message === 'string' && message.trim() ? message : fallback
  }
}
